import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { mustHavesForCategory } from '@/lib/mustHaves';
import { ListChecks, Plus, X, Check } from 'lucide-react';

// Login-time announcement + inline onboarding for Must-Haves (existing clients).
// STATE-BASED eligibility computed server-side (GET /client/must-haves-intro):
// authoring items via ANY path stops it automatically; 3-impression lifetime cap;
// "Don't show again" = permanent dismiss; snooze = nothing (impression already
// counted at show). Session guard is sessionStorage-only (dedupe, not the store —
// all flags live on the user doc, cross-device).
// CLIENT-FACING SURFACE: the editor-fee policy is never mentioned here.

const SESSION_KEY = 'ug_mh_intro_shown';
const MH_SOFT_CAP = 8;
const MH_HARD_CAP = 15;

export default function MustHavesIntro() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [intro, setIntro] = useState(null); // { brands: [...] }
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState('');
  const [items, setItems] = useState([]);
  const savedRef = useRef([]); // last persisted snapshot (rollback + dirty check)
  const [newText, setNewText] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimerRef = useRef(null);
  const cardRef = useRef(null);
  const newInputRef = useRef(null);

  // Once-per-session trigger. The session marker is set BEFORE the impression
  // write (optimistic — a failed write must never re-prompt this session).
  // impPostedRef guards the dev-only StrictMode double-mount from counting 2.
  const impPostedRef = useRef(false);
  useEffect(() => {
    if (user?.role !== 'client') return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    api.get('/client/must-haves-intro').then(r => {
      if (!r.data?.eligible) return;
      sessionStorage.setItem(SESSION_KEY, '1');
      setIntro(r.data);
      setBrand((r.data.brands || [''])[0] || '');
      setOpen(true);
      if (!impPostedRef.current) {
        impPostedRef.current = true;
        api.post('/client/must-haves-intro/impression').catch(() => {});
      }
    }).catch(() => {});
  }, [user?.role]);

  // Focus trap + Escape=snooze + autofocus (Apple polish: keyboard never leaves).
  useEffect(() => {
    if (!open) return;
    setTimeout(() => newInputRef.current?.focus(), 260);
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); return; }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const list = Array.from(cardRef.current.querySelectorAll('button, input, a, [tabindex]:not([tabindex="-1"])'))
        .filter(n => !n.disabled && n.offsetParent !== null);
      if (!list.length) return;
      const first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open]);

  // Multi-brand: load the selected brand's list on switch. Reads via the
  // lazy-migration alias — new by_category.highlight bucket if present, else
  // legacy flat must_haves. Modal ONLY surfaces the Highlight list (see
  // operator ruling 2026-07-15: simplest honest version for a login-time
  // popup; the other two categories live on the full Style Profile page).
  useEffect(() => {
    if (!open) return;
    api.get(`/style-profile${brand ? `?brand=${encodeURIComponent(brand)}` : ''}`).then(r => {
      // Shape aliasing lives in lib/mustHaves (single raw reader).
      const mh = mustHavesForCategory(r.data?.video, 'highlight');
      setItems(mh);
      savedRef.current = mh;
    }).catch(() => {});
  }, [open, brand]);

  // Same behavior as the Style Profile card: targeted PATCH persistence,
  // Saved ✓ flash, rollback on failure, adopt server-assigned ids. Writes
  // explicitly to the HIGHLIGHT category (server default matches, but being
  // explicit protects against silent future default changes).
  const persist = useCallback(async (next) => {
    const prev = savedRef.current;
    setItems(next);
    try {
      const r = await api.patch('/style-profile/must-haves', {
        brand,
        category: 'highlight',
        must_haves: next.map(m => ({ id: m.id || '', text: m.text, created_at: m.created_at || '' })),
      });
      // Shape aliasing lives in lib/mustHaves (single raw reader).
      const saved = mustHavesForCategory(r.data?.video, 'highlight');
      setItems(saved);
      savedRef.current = saved;
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      setSavedFlash(true);
      flashTimerRef.current = setTimeout(() => setSavedFlash(false), 1800);
    } catch (e) {
      setItems(prev);
      toast.error(e.response?.data?.detail || 'Must-have save failed — please try again');
    }
  }, [brand]);

  const addItem = () => {
    const text = newText.trim();
    if (!text) return;
    if (items.length >= MH_HARD_CAP) { toast.error(`Keep the list to ${MH_HARD_CAP} items or fewer`); return; }
    setNewText('');
    persist([...items, { id: '', text }]);
  };
  const removeItem = (idx) => persist(items.filter((_, i) => i !== idx));
  const editLocal = (idx, text) => setItems(prev => prev.map((m, i) => i === idx ? { ...m, text } : m));
  const commitEdits = () => {
    const savedTexts = savedRef.current.map(m => m.text).join('\u0000');
    const curTexts = items.map(m => m.text).join('\u0000');
    if (savedTexts === curTexts) return;
    persist(items.map(m => ({ ...m, text: m.text.trim() })).filter(m => m.text));
  };

  const snooze = () => setOpen(false); // impression already counted; reappears next session
  const dismissForever = () => {
    setOpen(false); // optimistic — session guard keeps it away regardless
    api.post('/client/must-haves-intro/dismiss').catch(() => {});
  };

  if (!open || !intro) return null;
  const brands = intro.brands || [''];
  const multiBrand = brands.length > 1;
  const hasItems = items.length > 0;

  return (
    <div className="ug-modal mh-intro-overlay fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="mh-intro-modal">
      <div ref={cardRef} role="dialog" aria-modal="true" aria-labelledby="mh-intro-title"
        className="mh-intro-enter w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl border border-ug-gold/30 bg-ug-deepest shadow-2xl">
        <div className="p-5 pb-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-8 h-8 rounded-lg bg-ug-gold/10 border border-ug-gold/25 flex items-center justify-center shrink-0">
                <ListChecks className="w-4 h-4 text-ug-gold" />
              </span>
              {/* .tap-auto: the global [data-testid] min-height:44px otherwise
                  inflates the h3 and rides the title high off the icon midline. */}
              <h3 data-role="section-title" id="mh-intro-title" className="tap-auto font-sans text-[15px] font-semibold text-zinc-100 leading-none whitespace-nowrap" data-testid="mh-intro-title">Introducing Must-Haves</h3>
              <span className="mh-new-badge tap-auto shrink-0 font-sans text-[8px] font-bold uppercase tracking-[0.12em] text-obsidian bg-gold-leaf rounded-full px-2 py-[3px] leading-none" data-testid="mh-intro-new-badge">New</span>
            </div>
            <button type="button" onClick={snooze} aria-label="Close" data-testid="mh-intro-close-btn"
              className="tap-auto text-slate-500 hover:text-zinc-200 transition-colors p-0.5 shrink-0"><X className="w-4 h-4" /></button>
          </div>
          <p className="tap-auto text-[12px] text-zinc-100/80 leading-relaxed mt-3" data-testid="mh-intro-body">
            Some things belong in every single film. Now you can make sure they're never missed. Add short, specific
            requirements — like "Weave in ambient audio throughout — cheers, laughter, vow reactions" — and your editor
            will confirm each one, item by item, before submitting your first draft. This list applies to your{' '}
            <strong className="text-ug-gold/90">Highlight Videos</strong>; you'll find separate lists for Doc Edits and
            Social Media Videos on your full Style Profile.
          </p>
        </div>

        <div className="px-5 pb-4">
          {multiBrand && (
            <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="mh-intro-brand-selector">
              <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 mr-1">Brand</span>
              {brands.map((b, i) => (
                <button key={b || 'main'} type="button" onClick={() => setBrand(b)} data-testid={`mh-intro-brand-pill-${i}`}
                  className={`tap-auto px-2.5 py-1 rounded-full font-sans text-[10px] font-medium border transition-colors ${brand === b ? 'bg-gold-leaf/15 border-gold-leaf/50 text-ug-gold' : 'border-ug-border text-slate-400 hover:border-ug-gold/40 hover:text-ug-gold'}`}>
                  {b || 'Main'}
                </button>
              ))}
            </div>
          )}

          {items.length > 0 && (
            <div className="space-y-2 mb-3" data-testid="mh-intro-list">
              {items.map((m, i) => (
                <div key={m.id || `new-${i}`} className="tap-auto flex items-center gap-2" data-testid={`mh-intro-row-${i}`}>
                  <input value={m.text} maxLength={140}
                    onChange={e => editLocal(i, e.target.value)}
                    onBlur={commitEdits}
                    data-testid={`mh-intro-input-${i}`}
                    className="tap-auto flex-1 bg-ug-surface border border-ug-border text-zinc-100 text-[12px] rounded-md px-3 py-2 focus:border-gold-leaf/40 outline-none transition-colors" />
                  <button type="button" onClick={() => removeItem(i)} data-testid={`mh-intro-remove-${i}`} aria-label="Remove item"
                    className="tap-auto text-slate-600 hover:text-ug-status-red transition-colors p-1"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input ref={newInputRef} value={newText} maxLength={140}
              onChange={e => setNewText(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
              placeholder={items.length >= MH_HARD_CAP ? `Limit of ${MH_HARD_CAP} items reached` : 'Add a must-have...'}
              disabled={items.length >= MH_HARD_CAP}
              data-testid="mh-intro-new-input"
              className="tap-auto flex-1 bg-ug-surface border border-ug-border text-zinc-100 text-[12px] rounded-md px-3 py-2 placeholder:text-white/35 focus:border-gold-leaf/40 outline-none transition-colors disabled:opacity-40" />
            <Button size="sm" variant="outline" onClick={addItem} disabled={!newText.trim() || items.length >= MH_HARD_CAP}
              data-testid="mh-intro-add-btn"
              className="tap-auto h-9 border-gold-leaf/25 text-ug-gold hover:bg-gold-leaf/10 font-sans text-[10px] font-semibold shrink-0">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className={`tap-auto font-mono text-[9px] ${items.length > MH_SOFT_CAP ? 'text-ug-gold' : 'text-slate-600'}`} data-testid="mh-intro-count">
              {items.length} of {MH_HARD_CAP}
              {items.length > MH_SOFT_CAP && ' — keep this to the true must-haves'}
            </span>
            <span aria-live="polite" data-testid="mh-intro-saved-indicator"
              className={`tap-auto font-sans text-[9px] font-semibold text-oxidized-teal transition-opacity duration-300 ${savedFlash ? 'opacity-100' : 'opacity-0'}`}>
              Saved ✓
            </span>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-ug-border">
          {hasItems ? (
            <div className="flex items-center gap-3" data-testid="mh-intro-success">
              <p className="tap-auto flex-1 text-[11px] text-oxidized-teal leading-snug font-medium">
                <Check className="w-3.5 h-3.5 inline mr-1 -mt-px" strokeWidth={2.5} />
                Your must-haves are saved — your editor will confirm each one before your first draft ✓
              </p>
              <Button onClick={() => setOpen(false)} data-testid="mh-intro-done-btn"
                className="bg-gold-leaf text-obsidian hover:bg-gold-leaf/90 font-sans text-[11px] font-semibold h-9 px-5 shrink-0">
                Done
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={snooze} data-testid="mh-intro-snooze-btn"
                  className="tap-auto border-ug-border text-slate-300 hover:text-zinc-100 font-sans text-[11px] font-medium h-9 px-4">
                  Remind me later
                </Button>
                <button type="button" onClick={dismissForever} data-testid="mh-intro-dismiss-link"
                  className="tap-auto font-sans text-[10px] text-slate-600 hover:text-slate-400 underline underline-offset-2 transition-colors">
                  Don't show this again
                </button>
              </div>
              <button type="button" onClick={() => { setOpen(false); navigate('/style-profile'); }} data-testid="mh-intro-profile-link"
                className="tap-auto font-sans text-[10px] text-ug-gold/70 hover:text-ug-gold transition-colors">
                Manage all three lists in your Style Profile →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
